import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import AdminLayout from "./AdminLayout";
import ApiService from "../../services/api";
import {
  Users,
  Package,
  ShoppingBag,
  Building2,
  TrendingUp,
  DollarSign,
  Activity,
  UserCheck,
} from "lucide-react";

interface DashboardStats {
  totalUsers: number;
  totalProducts: number;
  totalOrders: number;
  totalAccounts: number;
  totalRevenue: number;
  activeUsers: number;
  pendingOrders: number;
  growthRate: number;
  // Add change percentages
  usersChange: number;
  productsChange: number;
  ordersChange: number;
  accountsChange: number;
  revenueChange: number;
  activeUsersChange: number;
  pendingOrdersChange: number;
  growthRateChange: number;
}

interface ApiActivityItem {
  date_time?: string;
  status: "success" | "pending" | "failed";
  type: "order" | "user" | "patient" | "product";
  description?: string;
  order_id?: string;
  email?: string;
  timestamp?: string;
  id?: number;
}

interface ApiActivityResponse {
  last_completed_order?: ApiActivityItem;
  last_failed_order?: ApiActivityItem;
  new_user?: ApiActivityItem;
  pending_patient?: ApiActivityItem;
  updated_product_inventory?: ApiActivityItem;
}

interface ApiOverviewResponse {
  accounts: {
    change_percentage: number;
    current_month: number;
  };
  active_users: {
    change_percentage: number;
    current_month: number;
  };
  growth_rate: {
    current_month: number;
    growth_rate_percentage: number;
    last_month: number;
  };
  orders: {
    change_percentage: number;
    current_month: number;
  };
  pending_orders: {
    change_percentage: number;
    current_month: number;
  };
  products: {
    change_percentage: number;
    current_month: number;
  };
  revenue: {
    change_percentage: number;
    current_month: number;
  };
  users: {
    change_percentage: number;
    current_month: number;
  };
}

interface AdminDashboardProps {}

const AdminDashboard: React.FC<AdminDashboardProps> = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    totalProducts: 0,
    totalOrders: 0,
    totalAccounts: 0,
    totalRevenue: 0,
    activeUsers: 0,
    pendingOrders: 0,
    growthRate: 0,
    usersChange: 0,
    productsChange: 0,
    ordersChange: 0,
    accountsChange: 0,
    revenueChange: 0,
    activeUsersChange: 0,
    pendingOrdersChange: 0,
    growthRateChange: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [recentActivity, setRecentActivity] = useState<ApiActivityItem[]>([]);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [isLoadingActivity, setIsLoadingActivity] = useState(false);

  useEffect(() => {
    fetchDashboardStats();
    fetchRecentActivity();
  }, []);

  const fetchDashboardStats = async () => {
    setIsLoadingStats(true);
    try {
      const overviewResponse = await ApiService.overview({});

      if (overviewResponse.success && overviewResponse.data) {
        const data = overviewResponse.data;

        setStats({
          totalUsers: data.users.current_month,
          totalProducts: data.products.current_month,
          totalOrders: data.orders.current_month,
          totalAccounts: data.accounts.current_month,
          totalRevenue: data.revenue.current_month,
          activeUsers: data.active_users.current_month,
          pendingOrders: data.pending_orders.current_month,
          growthRate: data.growth_rate.current_month,
          usersChange: data.users.change_percentage,
          productsChange: data.products.change_percentage,
          ordersChange: data.orders.change_percentage,
          accountsChange: data.accounts.change_percentage,
          revenueChange: data.revenue.change_percentage,
          activeUsersChange: data.active_users.change_percentage,
          pendingOrdersChange: data.pending_orders.change_percentage,
          growthRateChange: data.growth_rate.growth_rate_percentage,
        });
      } else {
        throw new Error("Failed to fetch overview data");
      }
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
      // Set fallback data
      setStats({
        totalUsers: 0,
        totalProducts: 0,
        totalOrders: 0,
        totalAccounts: 0,
        totalRevenue: 0,
        activeUsers: 0,
        pendingOrders: 0,
        growthRate: 0,
        usersChange: 0,
        productsChange: 0,
        ordersChange: 0,
        accountsChange: 0,
        revenueChange: 0,
        activeUsersChange: 0,
        pendingOrdersChange: 0,
        growthRateChange: 0,
      });
    } finally {
      setIsLoadingStats(false);
    }
  };

  const fetchRecentActivity = async () => {
    setIsLoadingActivity(true);
    try {
      const activityResponse = await ApiService.recentActivity({});

      if (activityResponse.success && activityResponse.data) {
        const activities = transformApiDataToActivity(activityResponse.data);

        // If no activities from API, show empty state messages
        if (activities.length === 0) {
          setRecentActivity(getEmptyActivityMessages());
        } else {
          setRecentActivity(activities);
        }
      } else {
        setRecentActivity(getEmptyActivityMessages());
      }
    } catch (error) {
      console.error("Error fetching recent activity:", error);
      // Set static fallback data
      setRecentActivity([
        {
          id: 1,
          type: "user",
          description: "Unable to load recent user activities",
          timestamp: "Connection error",
          status: "failed",
        },
        {
          id: 2,
          type: "order",
          description: "Unable to load recent order activities",
          timestamp: "Connection error",
          status: "failed",
        },
        {
          id: 3,
          type: "patient",
          description: "Unable to load patient status updates",
          timestamp: "Connection error",
          status: "failed",
        },
      ]);
    } finally {
      setIsLoadingActivity(false);
    }
  };

  const formatChangePercentage = (percentage: number): string => {
    const sign = percentage > 0 ? "+" : "";
    return `${sign}${percentage.toFixed(0)}%`;
  };

  const getChangeType = (percentage: number): "positive" | "negative" => {
    return percentage >= 0 ? "positive" : "negative";
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case "user":
        return <Users className="h-4 w-4" />;
      case "order":
        return <ShoppingBag className="h-4 w-4" />;
      case "product":
        return <Package className="h-4 w-4" />;
      case "patient":
        return <Building2 className="h-4 w-4" />;
      default:
        return <Activity className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case "success":
        return "text-green-600 bg-green-100";
      case "pending":
        return "text-yellow-600 bg-yellow-100";
      case "failed":
        return "text-red-600 bg-red-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  if (isLoading) {
    return (
      <AdminLayout title="Dashboard">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  const getRelativeTime = (dateTime?: string) => {
    const now = new Date();
    const date = new Date(dateTime ? dateTime : new Date());
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60)
      return `${diffInMinutes} minute${diffInMinutes > 1 ? "s" : ""} ago`;
    if (diffInHours < 24)
      return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
    return `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;
  };

  const transformApiDataToActivity = (
    apiData: ApiActivityResponse
  ): ApiActivityItem[] => {
    const activities: ApiActivityItem[] = [];

    if (apiData.new_user) {
      activities.push({
        id: 1,
        type: "user",
        description: `New user registered: ${apiData.new_user.email}`,
        timestamp: getRelativeTime(apiData.new_user.date_time),
        status: apiData.new_user.status,
      });
    } else {
      activities.push({
        id: 1,
        type: "user",
        description: "No recent user registrations to display",
        timestamp: "No activity",
        status: "pending",
      });
    }

    if (apiData.last_completed_order) {
      activities.push({
        id: 2,
        type: "order",
        description: `Order #${apiData.last_completed_order.order_id} completed successfully`,
        timestamp: getRelativeTime(apiData.last_completed_order.date_time),
        status: apiData.last_completed_order.status,
      });
    } else {
      activities.push({
        id: 2,
        type: "order",
        description: "No recent order completions to show",
        timestamp: "No activity",
        status: "pending",
      });
    }

    if (apiData.last_failed_order) {
      activities.push({
        id: 3,
        type: "order",
        description: `Payment failed for order #${apiData.last_failed_order.order_id}`,
        timestamp: getRelativeTime(apiData.last_failed_order.date_time),
        status: apiData.last_failed_order.status,
      });
    } else {
      activities.push({
        id: 3,
        type: "order",
        description: "No recent failed orders to show",
        timestamp: "No activity",
        status: "pending",
      });
    }

    if (apiData.pending_patient) {
      activities.push({
        id: 4,
        type: "patient",
        description: `New patient #${apiData.pending_patient.id} is awaiting approval`,
        timestamp: getRelativeTime(apiData.pending_patient.date_time),
        status: apiData.pending_patient.status,
      });
    } else {
      activities.push({
        id: 4,
        type: "patient",
        description: "No patients are currently pending approval",
        timestamp: "No activity",
        status: "pending",
      });
    }

    if (apiData.updated_product_inventory) {
      activities.push({
        id: 5,
        type: "product",
        description: `Product inventory updated for product #${apiData.updated_product_inventory.id}`,
        timestamp: getRelativeTime(apiData.updated_product_inventory.date_time),
        status: apiData.updated_product_inventory.status,
      });
    } else {
      activities.push({
        id: 5,
        type: "product",
        description: "No recent product inventory updates",
        timestamp: "No activity",
        status: "pending",
      });
    }

    // Sort by date (most recent first)
    return activities.sort((a, b) => {
      const getDateTime = (activity: ApiActivityItem) => {
        if (activity.type === "user" && apiData.new_user)
          return apiData.new_user.date_time;
        if (
          activity.type === "order" &&
          activity.status === "success" &&
          apiData.last_completed_order
        )
          return apiData.last_completed_order.date_time;
        if (
          activity.type === "order" &&
          activity.status === "failed" &&
          apiData.last_failed_order
        )
          return apiData.last_failed_order.date_time;
        if (activity.type === "patient" && apiData.pending_patient)
          return apiData.pending_patient.date_time;
        if (activity.type === "product" && apiData.updated_product_inventory)
          return apiData.updated_product_inventory.date_time;
        return new Date().toISOString();
      };

      return (
        new Date(getDateTime(b) ?? "").getTime() -
        new Date(getDateTime(a) ?? "").getTime()
      );
    });
  };

  const getEmptyActivityMessages = (): ApiActivityItem[] => [
    {
      id: 1,
      type: "user",
      description: "No recent user registrations to display",
      timestamp: "No activity",
      status: "pending",
    },
    {
      id: 2,
      type: "order",
      description: "No recent order completions to show",
      timestamp: "No activity",
      status: "pending",
    },
    {
      id: 3,
      type: "patient",
      description: "No patients are currently pending approval",
      timestamp: "No activity",
      status: "pending",
    },
    {
      id: 4,
      type: "product",
      description: "No recent product inventory updates",
      timestamp: "No activity",
      status: "pending",
    },
    {
      id: 5,
      type: "order",
      description: "No recent failed orders to show",
      timestamp: "No activity",
      status: "pending",
    },
  ];

  const statCards = [
    {
      title: "Total Users",
      value: stats.totalUsers,
      icon: Users,
      color: "bg-blue-500",
      change: formatChangePercentage(stats.usersChange),
      changeType: getChangeType(stats.usersChange),
    },
    {
      title: "Total Products",
      value: stats.totalProducts,
      icon: Package,
      color: "bg-green-500",
      change: formatChangePercentage(stats.productsChange),
      changeType: getChangeType(stats.productsChange),
    },
    {
      title: "Total Orders",
      value: stats.totalOrders,
      icon: ShoppingBag,
      color: "bg-purple-500",
      change: formatChangePercentage(stats.ordersChange),
      changeType: getChangeType(stats.ordersChange),
    },
    {
      title: "Total Accounts",
      value: stats.totalAccounts,
      icon: Building2,
      color: "bg-orange-500",
      change: formatChangePercentage(stats.accountsChange),
      changeType: getChangeType(stats.accountsChange),
    },
    {
      title: "Total Revenue",
      value: `$${stats.totalRevenue.toLocaleString()}`,
      icon: DollarSign,
      color: "bg-emerald-500",
      change: formatChangePercentage(stats.revenueChange),
      changeType: getChangeType(stats.revenueChange),
    },
    {
      title: "Active Users",
      value: stats.activeUsers,
      icon: UserCheck,
      color: "bg-cyan-500",
      change: formatChangePercentage(stats.activeUsersChange),
      changeType: getChangeType(stats.activeUsersChange),
    },
    {
      title: "Pending Orders",
      value: stats.pendingOrders,
      icon: Activity,
      color: "bg-yellow-500",
      change: formatChangePercentage(stats.pendingOrdersChange),
      changeType: getChangeType(stats.pendingOrdersChange),
    },
    {
      title: "Growth Rate",
      value: `${stats.growthRate}%`,
      icon: TrendingUp,
      color: "bg-pink-500",
      change: formatChangePercentage(stats.growthRateChange),
      changeType: getChangeType(stats.growthRateChange),
    },
  ];

  return (
    <AdminLayout title="Dashboard">
      <div className="space-y-6">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 text-white">
          <div className="max-w-3xl">
            <h1 className="text-3xl font-bold mb-2">Welcome back, Admin!</h1>
            <p className="text-blue-100 text-lg">
              Here's what's happening with your platform today.
            </p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {isLoadingStats
            ? // Show skeleton loaders
              Array.from({ length: 8 }).map((_, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 animate-pulse"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                      <div className="h-8 bg-gray-200 rounded w-16 mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded w-20"></div>
                    </div>
                    <div className="h-12 w-12 bg-gray-200 rounded-xl"></div>
                  </div>
                </div>
              ))
            : statCards.map((card, index) => {
                const Icon = card.icon;
                return (
                  <div
                    key={index}
                    className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-600 mb-1">
                          {card.title}
                        </p>
                        <p className="text-2xl font-bold text-gray-900">
                          {card.value}
                        </p>
                        <div className="flex items-center mt-2">
                          <span
                            className={`text-sm font-medium ${
                              card.changeType === "positive"
                                ? "text-green-600"
                                : "text-red-600"
                            }`}
                          >
                            {card.change}
                          </span>
                          <span className="text-sm text-gray-500 ml-1">
                            vs last month
                          </span>
                        </div>
                      </div>
                      <div className={`${card.color} p-3 rounded-xl`}>
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                    </div>
                  </div>
                );
              })}
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Activity Feed */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  Recent Activity
                </h2>
              </div>
              <div className="p-6">
                {isLoadingActivity ? (
                  // Activity loader
                  <div className="space-y-4">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <div
                        key={index}
                        className="flex items-start space-x-3 animate-pulse"
                      >
                        <div className="h-8 w-8 bg-gray-200 rounded-full"></div>
                        <div className="flex-1 space-y-2">
                          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                          <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : recentActivity.length > 0 ? (
                  <div className="flow-root">
                    <ul className="-mb-8">
                      {recentActivity.map((activity, index) => (
                        <li key={activity.id}>
                          <div className="relative pb-8">
                            {index !== recentActivity.length - 1 ? (
                              <span
                                className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200"
                                aria-hidden="true"
                              />
                            ) : null}
                            <div className="relative flex items-start space-x-3">
                              <div
                                className={`relative px-1 ${getStatusColor(
                                  activity.status
                                )} rounded-full flex items-center justify-center h-8 w-8`}
                              >
                                {getActivityIcon(activity.type)}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div>
                                  <p className="text-sm text-gray-900">
                                    {activity.description}
                                  </p>
                                  <p className="text-sm text-gray-500">
                                    {activity.timestamp}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Activity className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                      No recent activity
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Platform activity will appear here as users interact with
                      the system.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  Quick Actions
                </h2>
              </div>
              <div className="p-6 space-y-3">
                <button
                  className="w-full flex items-center px-4 py-3 text-left border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                  onClick={() => {
                    user?.role === "admin"
                      ? navigate("/admin/users?is_add=true")
                      : navigate(
                          `/account/accounts/${user?.clinic_id}?is_add_user=true`
                        );
                  }}
                >
                  <Users className="h-5 w-5 text-blue-600 mr-3" />
                  <span className="text-sm font-medium text-gray-900">
                    Add New User
                  </span>
                </button>
                <button
                  className="w-full flex items-center px-4 py-3 text-left border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                  onClick={() => {
                    user?.role === "admin"
                      ? navigate("/admin/products?is_add=true")
                      : navigate(
                          `/account/accounts/${user?.clinic_id}?is_view_products=true`
                        );
                  }}
                >
                  <Package className="h-5 w-5 text-green-600 mr-3" />
                  <span className="text-sm font-medium text-gray-900">
                    {user?.role === "admin"
                      ? "Add New Product"
                      : "View Products"}
                  </span>
                </button>
                <button
                  className="w-full flex items-center px-4 py-3 text-left border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                  onClick={() => {
                    user?.role === "admin"
                      ? navigate("/admin/accounts?is_add=true")
                      : navigate(
                          `/account/accounts/${user?.clinic_id}?is_view_details=true`
                        );
                  }}
                >
                  <Building2 className="h-5 w-5 text-purple-600 mr-3" />
                  <span className="text-sm font-medium text-gray-900">
                    {user?.role === "admin"
                      ? "Onboard Account"
                      : "View Account"}
                  </span>
                </button>
                <button
                  className="w-full flex items-center px-4 py-3 text-left border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                  onClick={() => {
                    user?.role === "admin"
                      ? navigate("/admin/orders?is_pending=true")
                      : navigate(`/account/orders?is_pending=true`);
                  }}
                >
                  <ShoppingBag className="h-5 w-5 text-orange-600 mr-3" />
                  <span className="text-sm font-medium text-gray-900">
                    View Pending Orders
                  </span>
                </button>
              </div>
            </div>

            {/* System Status */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  System Status
                </h2>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">API Status</span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Operational
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Database</span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Healthy
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Payment Gateway</span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Active
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Email Service</span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Active
                  </span>
                  {/* <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    Degraded
                  </span> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
